/**
 * Cleanup: recognised callers with no account yet (GoTo contacts, new callers logged from the
 * desk). Not a chore list: the queue sorts by who called most recently, a rule links the clear
 * ones (dist/scripts/draft-autolink.js), and the rest resolve on the call pop or here. Nothing
 * creates an account; a store that is not on the book stays here until the sweep brings it in.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { DraftCard } from '../components/cleanup/draft-card';
import { Button } from '../components/ui/button';
import { useProvideAssistantContext } from '../lib/assistant-context';
import { draftsQuery } from '../lib/queries';
import { describeRpcError, rpc } from '../lib/rpc';

export const Route = createFileRoute('/cleanup')({
  component: Cleanup,
});

function Cleanup() {
  const qc = useQueryClient();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const q = useQuery({ ...draftsQuery(50, cursor), placeholderData: keepPreviousData });
  useProvideAssistantContext(null);
  useRowKeys();
  const bulk = useMutation({
    mutationFn: (ids: string[]) => rpc.drafts.dismissMany({ draftIds: ids }),
    onSuccess: () => {
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['drafts.list'] });
    },
  });

  if (q.error) return <div className="px-6 pt-6 text-body text-alert">{describeRpcError(q.error)}</div>;
  if (!q.data) return <div className="px-6 pt-6 text-body text-ink-3">Loading the queue</div>;
  const { rows, total, buckets, nextCursor } = q.data;
  const closedOnPage = rows.filter((r) => r.bucket === 'closed_only').map((r) => r.draftId);

  return (
    <div className="mx-auto w-full max-w-[1120px] px-6 pt-4 pb-12">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h1 className="text-title font-medium">Cleanup</h1>
        <span className="text-hint text-ink-3">
          {total} recognised {total === 1 ? 'caller' : 'callers'} without an account
          {total > 0 ? `: ${buckets.live} with a live match, ${buckets.closed_only} closed only, ${buckets.none} with no match` : ''}
          {rows.length < total ? `. Showing ${rows.length}` : ''}
        </span>
        <span className="ml-auto flex items-center gap-2">
          {closedOnPage.length > 0 && selected.size === 0 && (
            <Button variant="ghost" onClick={() => { setSelected(new Set(closedOnPage)); }}>
              Select the {closedOnPage.length} closed only
            </Button>
          )}
          {selected.size > 0 && (
            <>
              <Button variant="ghost" onClick={() => { setSelected(new Set()); }}>Clear</Button>
              <Button variant="primary" disabled={bulk.isPending} onClick={() => { bulk.mutate([...selected]); }}>
                Dismiss {selected.size} selected
              </Button>
            </>
          )}
        </span>
      </div>
      <p className="mt-1 text-body text-ink-2">
        The question for each row is which account should pop when this number calls. Most recent callers are first. Link it, pick a location, or dismiss it; the rest resolve from the call pop. Keys on a row: L link the suggestion, D dismiss, N not a merchant, X select.
      </p>
      {bulk.error && <p className="mt-2 text-body text-alert">{describeRpcError(bulk.error)}</p>}
      {bulk.data && <p className="mt-2 text-body text-ink-2">Dismissed {bulk.data.dismissed}{bulk.data.skipped ? `, ${bulk.data.skipped} already handled by someone else` : ''}.</p>}

      {rows.length === 0 ? (
        <p className="mt-8 text-body text-ink-3">Nothing to clean up.</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-3">
          {rows.map((d) => (
            <DraftCard
              key={d.draftId}
              d={d}
              selected={selected.has(d.draftId)}
              onSelect={(on) => {
                setSelected((s) => {
                  const next = new Set(s);
                  if (on) next.add(d.draftId);
                  else next.delete(d.draftId);
                  return next;
                });
              }}
              onDone={() => {
                setSelected((s) => {
                  if (!s.has(d.draftId)) return s;
                  const next = new Set(s);
                  next.delete(d.draftId);
                  return next;
                });
              }}
            />
          ))}
        </ul>
      )}
      {nextCursor && (
        <Button className="mt-4" onClick={() => { setCursor(nextCursor); }}>
          Next {Math.min(50, total - rows.length)}
        </Button>
      )}
    </div>
  );
}

/** Up and down walk the rows when a row has focus; the row's own keys do the rest. */
function useRowKeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      if (!(el instanceof HTMLElement) || !el.hasAttribute('data-draft')) return;
      const rows = Array.from(document.querySelectorAll<HTMLElement>('[data-draft]'));
      const i = rows.indexOf(el);
      let next: HTMLElement | undefined;
      if (e.key === 'ArrowDown' || e.key === 'j') next = rows[i + 1];
      else if (e.key === 'ArrowUp' || e.key === 'k') next = rows[i - 1];
      else return;
      if (!next) return;
      e.preventDefault();
      next.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);
}
